import motion from "../../motion-tokens.json" with { type: "json" };
import { createEchartsLifecycle, memoisedImport } from "../../charts/echarts-lifecycle.js";
import { AXIS_LABEL_COLOR, categoryAxis, prefersMotion, valueAxis } from "../../charts/cartesian-chrome.js";
import { insightsAxisTooltip } from "../../charts/insights-chart-tooltip.js";

/**
 * Check my results' topics over time (S40): one line per chosen topic, of its clicks by week, drawn by the
 * app's own echarts from TopicTrendChart::payload(). Hovering shows the shared dark tooltip
 * (charts/insights-chart-tooltip.js).
 *
 * It draws the payload it starts with and nothing else: Blade keys the component by its payload, so a new
 * window or topic mounts a new chart, and destroy() tears this one down.
 *
 * Prefix: none. Its one reactive property is `failed`, which the Blade's message reads.
 */

/**
 * echarts only once Check my results draws, never on every page: this module is reachable from app.js.
 * The metric screen's registration already holds the line, the grid and the tooltip this needs.
 */
const loadECharts = memoisedImport(() => import("../../charts/echarts-metric-chart"));

/**
 * A tick in the workspace's notation: thousands grouped, and a decimal only where the tick has one.
 *
 * @param {number} value
 * @param {{thousands: string, decimal: string}} separators
 */
export function formatTick(value, { thousands, decimal }) {
    const fixed = Number.isInteger(value) ? String(Math.abs(value)) : Math.abs(value).toFixed(1);
    const [whole, fraction] = fixed.split(".");
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);

    return (value < 0 ? "-" : "") + (fraction === undefined ? grouped : grouped + decimal + fraction);
}

/**
 * Whether a week's figure stands alone, between two weeks with none: a line needs two points, so that
 * week is drawn as a dot or it would not show at all.
 */
const standsAlone = (values, index) =>
    values[index] !== null
    && (index === 0 || values[index - 1] === null)
    && (index === values.length - 1 || values[index + 1] === null);

/**
 * @param {object}  payload  TopicTrendChart::payload().
 * @param {boolean} animate  False under reduced motion: nothing grows or fades in.
 */
export function buildTopicTrendOption(payload, animate = prefersMotion()) {
    const { labels, weeks, series, separators } = payload;

    return {
        animation: animate,
        animationDuration: motion.duration.view,
        animationEasing: "cubicOut",
        grid: { left: 0, right: 8, top: 8, bottom: 0, containLabel: true },
        xAxis: categoryAxis(labels, {
            boundaryGap: false,
            // The last week's tick sits on the chart's right edge, so its label ends there rather than
            // centring on it and running off the card ("28 Se").
            axisLabel: { color: AXIS_LABEL_COLOR, fontSize: 11, hideOverlap: true, alignMaxLabel: "right" },
        }),
        yAxis: valueAxis({
            // Clicks are whole: never a tick between two.
            minInterval: 1,
            splitNumber: 3,
            axisLabel: {
                color: AXIS_LABEL_COLOR,
                fontSize: 11,
                formatter: (value) => formatTick(value, separators),
            },
        }),
        tooltip: insightsAxisTooltip(
            (index) => weeks[index],
            (index) => series.map((line) => ({ name: line.name, color: line.color, value: line.formatted[index] })),
        ),
        series: series.map((line) => ({
            type: "line",
            name: line.name,
            data: line.values,
            connectNulls: false,
            symbol: "circle",
            symbolSize: (value, params) => (standsAlone(line.values, params.dataIndex) ? 6 : 0),
            lineStyle: { width: 2, color: line.color },
            itemStyle: { color: line.color },
            emphasis: { disabled: true },
        })),
    };
}

export default function InsightsTopicTrendChart(payload) {
    // Kept in the closure, not on the component: Alpine deep-proxies its properties, and a live echarts
    // instance must not be proxied (echarts-lifecycle.js).
    let lifecycle = null;
    let renderToken = 0;

    return {
        failed: false,

        init() {
            lifecycle = createEchartsLifecycle({
                label: "Check my results topic chart",
                getContainer: () => this.$refs.canvas,
                isCurrent: (token) => token === renderToken,
                invalidate: () => {
                    renderToken += 1;
                },
                rerender: () => this.draw(),
                onError: () => {
                    this.failed = true;
                },
            });

            this.draw();
        },

        async draw() {
            const token = ++renderToken;
            let echarts;

            try {
                echarts = (await loadECharts()).default;
            } catch (error) {
                console.error("The Check my results topic chart could not load echarts:", error);
                this.failed = true;

                return;
            }

            if (token !== renderToken) {
                return;
            }

            lifecycle.drawWhenBoxed(echarts, this.$refs.canvas, buildTopicTrendOption(payload), token);
        },

        destroy() {
            lifecycle?.cleanup();
        },
    };
}
